/**
 * Audio presets — capture constraints and Opus encoder settings, as one choice.
 *
 * The Settings panel has had a "Conference audio / Speech audio / Music audio"
 * dropdown for a while, parked and commented out because nothing was behind it.
 * This is what goes behind it.
 *
 * The values are not a port. edumeet's equivalent presets are the model for the
 * SHAPE — one named object carrying both the getUserMedia constraints and the
 * Opus options, so the two cannot drift apart — but its conference numbers are
 * weaker than ours: it sets no `opusNack` (which makes mediasoup-client strip
 * NACK from Opus entirely) and no bitrate floor (so congestion can starve the
 * encoder to a muffled sub-16 kbps). `conference` below is exactly what this
 * app already published; the other two are new.
 */

import { isPhone } from '../services/device-capability'

export type AudioProcessing = {
  echoCancellation: boolean
  noiseSuppression: boolean
  autoGainControl: boolean
}

/** Capture format. Real getUserMedia constraints, spread in as they are. */
export type AudioFormat = {
  sampleRate: number
  channelCount: number
  sampleSize: number
}

/**
 * The Opus mime string, spelled once.
 *
 * Named so the microphone can select Opus explicitly at produce time rather
 * than inheriting it from the router's codec ordering. The router also
 * advertises G722, PCMU and PCMA — those exist for H.323 interop and must
 * never be what a browser publishes.
 */
export const OPUS_MIME = 'audio/opus'

/**
 * Opus encoder options, passed as `codecOptions` at produce time.
 *
 * Set as a block, always. mediasoup-client clones the ROUTER's advertised fmtp
 * into the remote answer when `codecOptions` is absent, so the SFU's
 * capabilities would silently become every client's effective settings.
 */
export type OpusOptions = {
  opusStereo: boolean
  opusDtx: boolean
  opusFec: boolean
  opusNack: boolean
  opusPtime: number
  opusMaxPlaybackRate: number
  opusMaxAverageBitrate: number
}

export type AudioPresetName = 'conference' | 'speech' | 'music'

export type AudioPreset = {
  label: string
  description: string
  processing: AudioProcessing
  /** RNNoise (DeepFilterNet3). Never on at the same time as the browser's own. */
  noiseCancellation: boolean
  format: AudioFormat
  opus: OpusOptions
}

/**
 * RNNoise is off by default on a phone browser; the browser's own suppressor
 * (`noiseSuppression`) covers it there instead.
 *
 * Measured 2026-10-16 on two phones: the DeepFilterNet worklet alone held half
 * a core (Nothing Phone 2) to three quarters of one (moto g34), even while
 * muted, and was the largest single cause of the heating. It stays one switch
 * away in Settings. Set in the presets rather than only in the store's initial
 * state so that picking "Conference" on a phone does not turn it back on and
 * the panel still names the preset instead of "Custom".
 */
const RNNOISE_BY_DEFAULT = !isPhone()

export const AUDIO_PRESETS: Record<AudioPresetName, AudioPreset> = {
  /** What this app has always published. The default, and the safe one. */
  conference: {
    label: 'Conference audio',
    description: 'Voices in a room. Echo cancellation and noise removal on.',
    processing: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    noiseCancellation: RNNOISE_BY_DEFAULT,
    format: { sampleRate: 48000, channelCount: 1, sampleSize: 16 },
    opus: {
      opusStereo: false,
      opusDtx: false,
      opusFec: true,
      opusNack: true,
      opusPtime: 20,
      opusMaxPlaybackRate: 48000,
      opusMaxAverageBitrate: 40000,
    },
  },

  /**
   * For a bad link. Narrower band and half the bitrate.
   *
   * DTX stays OFF, which is where this deliberately parts company with
   * edumeet's `low` preset. DTX stops sending during whatever the encoder's
   * VAD calls silence, and it clips word onsets and eats soft speech — a
   * "speech" preset that damages speech to save bandwidth has the trade the
   * wrong way round. The saving comes from the bitrate and the playback rate,
   * which cost clarity honestly and predictably.
   */
  speech: {
    label: 'Speech audio',
    description: 'For a weak connection. Narrower and quieter, still continuous.',
    processing: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    noiseCancellation: RNNOISE_BY_DEFAULT,
    format: { sampleRate: 24000, channelCount: 1, sampleSize: 16 },
    opus: {
      opusStereo: false,
      opusDtx: false,
      opusFec: true,
      opusNack: true,
      opusPtime: 20,
      opusMaxPlaybackRate: 24000,
      opusMaxAverageBitrate: 20000,
    },
  },

  /**
   * For playing something into the meeting. Every filter off.
   *
   * Echo cancellation, noise suppression, gain control and RNNoise all exist to
   * make one talking person intelligible, and all four wreck music: AEC treats
   * sustained notes as echo, the denoisers treat instruments as noise, and AGC
   * pumps the dynamics flat.
   *
   * The cost is that nothing is cancelling your speakers any more, so this
   * REQUIRES headphones — without them the room hears itself back. The
   * description says so because the setting cannot enforce it.
   */
  music: {
    label: 'Music audio',
    description: 'Stereo, no filtering. Headphones required — echo cancellation is off.',
    processing: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    noiseCancellation: false,
    format: { sampleRate: 48000, channelCount: 2, sampleSize: 16 },
    opus: {
      opusStereo: true,
      opusDtx: false,
      opusFec: true,
      opusNack: true,
      opusPtime: 20,
      opusMaxPlaybackRate: 48000,
      opusMaxAverageBitrate: 128000,
    },
  },
}

export const DEFAULT_AUDIO_PRESET: AudioPresetName = 'conference'

/**
 * Which preset these settings ARE, or null if they no longer match one.
 *
 * The individual switches stay editable, so the moment someone turns echo
 * cancellation back on inside "Music audio" the label is a lie. The panel shows
 * "Custom" instead of naming a preset the audio is not using.
 */
export function matchAudioPreset(
  processing: AudioProcessing,
  noiseCancellation: boolean,
  format: AudioFormat,
): AudioPresetName | null {
  const names = Object.keys(AUDIO_PRESETS) as AudioPresetName[]
  return (
    names.find((name) => {
      const preset = AUDIO_PRESETS[name]
      return (
        preset.processing.echoCancellation === processing.echoCancellation &&
        preset.processing.noiseSuppression === processing.noiseSuppression &&
        preset.processing.autoGainControl === processing.autoGainControl &&
        preset.noiseCancellation === noiseCancellation &&
        preset.format.sampleRate === format.sampleRate &&
        preset.format.channelCount === format.channelCount &&
        preset.format.sampleSize === format.sampleSize
      )
    }) ?? null
  )
}
